import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, Card, Image, Space, Spin, Tag } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, CreditCardOutlined, FileTextOutlined } from '@ant-design/icons';
import Title from 'antd/lib/typography/Title';
import BookyBox from '../../Components/Layout/BookyBox';
import PagePublicSite from '../../Components/Page/PagePublicSite';
import { PublicSiteMenu } from '../../Components/PublicSite/PublicSiteMenu';
import NotFound from '../../Components/Page/NotFoundPage';
import { TextService } from '../../Services/TextService';
import { getCurrentLanguage } from '../../Helpers/RouteHelper';
import { PublicSiteService } from '../../Services/PublicSiteService';
import { WebApiCompanyDtoCompanyPublicSiteDto, WebApiPublicSiteDtoPublicPaymentYearDto } from '../../Repository/eventbookyapi';
import { IntegrationType } from '../../Types/IntegrationType';

enum State {
    Loading,
    CompanyNotFound,
    Ready,
    LoadError
}

/**
 * Own route/tab (`/payments`) where a logged-in member sees their membership fee status per year,
 * with a pay button for an outstanding Ping Payments link when the company allows it. Which years
 * are shown is decided by the API (current year always, next year once there is something for it,
 * previous year only when the company has opted in). Only reachable when the session says
 * payments are shown - redirects back to the profile page otherwise.
 */
const PublicSitePaymentsPage = () => {
    const params = useParams();
    const navigation = useNavigate();
    const GetText = TextService.GetText;

    const [state, setState] = useState(State.Loading);
    const [publicSite, setPublicSite] = useState({} as WebApiCompanyDtoCompanyPublicSiteDto);
    const [years, setYears] = useState([] as WebApiPublicSiteDtoPublicPaymentYearDto[]);
    const [isFamilyMember, setIsFamilyMember] = useState(false);

    useEffect(() => {
        const loadData = async () => {
            if (!params.companyurl) {
                setState(State.CompanyNotFound);
                return;
            }

            try {
                const company = await PublicSiteService.GetCompanyPublicSiteByUrl(params.companyurl);
                setPublicSite(company);

                const session = await PublicSiteService.GetSession();

                if (!session.showPayments) {
                    navigation('/' + getCurrentLanguage() + '/' + params.companyurl + '/editmember', { replace: true });
                    return;
                }

                setIsFamilyMember(session.isFamilyMember ?? false);
            } catch {
                setState(State.CompanyNotFound);
                return;
            }

            try {
                const result = await PublicSiteService.GetMemberPayments();
                // Newest year first.
                setYears([...(result.years ?? [])].sort((a, b) => (b.year ?? 0) - (a.year ?? 0)));
                setState(State.Ready);
            } catch {
                setState(State.LoadError);
            }
        };

        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params.companyurl]);

    const getDownloadUrl = (dto: WebApiCompanyDtoCompanyPublicSiteDto) => {
        return process.env.REACT_APP_API_URL + '/Image/downloadImage?bloburl=' + dto.siteLogoPath;
    };

    const formatAmount = (amount?: number) => amount && amount > 0 ? amount.toLocaleString('sv-SE') + ' kr' : '';

    const renderYear = (year: WebApiPublicSiteDtoPublicPaymentYearDto) => {
        const title = GetText('PublicSitePayments_Year').replace('{0}', (year.year ?? '').toString());
        const isFortnoxInvoice = !year.isPaid && year.source === IntegrationType.Fortnox;

        return (
            <Card key={year.year} size="small" title={title} style={{ marginBottom: '16px' }}>
                <Space direction="vertical" size="small">
                    {year.isPaid ? (
                        <span>
                            <CheckCircleOutlined style={{ color: 'green' }} /> {GetText('PublicSitePayments_Paid')}
                            {formatAmount(year.amount) ? <> &middot; {formatAmount(year.amount)}</> : ''}
                        </span>
                    ) : (
                        <span>
                            <CloseCircleOutlined style={{ color: 'red' }} /> {GetText('PublicSitePayments_NotPaid')}
                            {formatAmount(year.amount) ? <> &middot; {formatAmount(year.amount)}</> : ''}
                            {isFortnoxInvoice ? <Tag style={{ marginLeft: '8px' }}>{GetText('PublicSitePayments_InvoiceSent')}</Tag> : ''}
                        </span>
                    )}

                    {year.payUrl ? (
                        // A plain button (not href/<a>) so the public site's link styling doesn't recolor the label.
                        <Button type="primary" icon={<CreditCardOutlined />} onClick={() => window.open(year.payUrl!, '_blank', 'noopener,noreferrer')}>
                            {GetText('PublicSitePayments_Pay')}
                        </Button>
                    ) : ''}

                    {year.isPaid && year.invoiceUrl ? (
                        <Button type="link" icon={<FileTextOutlined />} href={year.invoiceUrl} target="_blank" rel="noopener noreferrer" style={{ padding: 0 }}>
                            {GetText('PublicSitePayments_Receipt')}
                        </Button>
                    ) : ''}
                </Space>
            </Card>
        );
    };

    if (state === State.CompanyNotFound) {
        return <NotFound message={GetText('PublicSiteEditMember_CompanyNotFound')} />;
    }

    if (state === State.Loading) {
        return (
            <PagePublicSite>
                <div style={{ textAlign: 'center', padding: '100px' }}>
                    <Spin size="large" />
                </div>
            </PagePublicSite>
        );
    }

    return (
        <PagePublicSite>
            {publicSite.siteLogoPath ? (
                <div style={{ marginBottom: '20px', textAlign: 'center' }}>
                    <Image preview={false} className='company-logo' src={getDownloadUrl(publicSite)} />
                </div>
            ) : (
                <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                    <Title style={{ margin: 0 }}>{publicSite.siteTitle}</Title>
                </div>
            )}
            <PublicSiteMenu />

            <BookyBox>
                <h2>{GetText('PublicSitePayments_Title')}</h2>

                {state === State.LoadError ? (
                    <Alert type="error" showIcon message={GetText('PublicSitePayments_LoadError')} />
                ) : (
                    <>
                        {isFamilyMember ? (
                            <Alert type="info" showIcon message={GetText('PublicSitePayments_FamilyMemberInfo')} style={{ marginBottom: '16px' }} />
                        ) : ''}
                        {years.map(renderYear)}
                    </>
                )}
            </BookyBox>
        </PagePublicSite>
    );
};

export default PublicSitePaymentsPage;
